import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Palette, Upload } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FONT_FAMILIES = [
  { value: 'Inter, sans-serif', label: 'Inter (Default)' },
  { value: 'Plus Jakarta Sans, sans-serif', label: 'Plus Jakarta Sans' },
  { value: 'Playfair Display, serif', label: 'Playfair Display' },
  { value: 'Merriweather, serif', label: 'Merriweather' },
  { value: 'Manrope, sans-serif', label: 'Manrope' },
  { value: 'Work Sans, sans-serif', label: 'Work Sans' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: 'Open Sans, sans-serif', label: 'Open Sans' },
  { value: 'Lato, sans-serif', label: 'Lato' },
  { value: 'Poppins, sans-serif', label: 'Poppins' },
];

const PRESET_COLORS = [
  { name: 'White', value: '#FFFFFF' },
  { name: 'Light Gray', value: '#F5F5F5' },
  { name: 'Cream', value: '#FFF8DC' },
  { name: 'Light Blue', value: '#E6F3FF' },
  { name: 'Light Green', value: '#E8F5E9' },
  { name: 'Light Pink', value: '#FCE4EC' },
  { name: 'Light Purple', value: '#F3E5F5' },
];

const TEXT_COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'Dark Gray', value: '#374151' },
  { name: 'Navy', value: '#1e3a8a' },
  { name: 'Brown', value: '#78350f' },
  { name: 'Dark Green', value: '#14532d' },
];

const ThemeCustomization = () => {
  const [theme, setTheme] = useState({
    background_color: '#FFFFFF',
    background_image: '',
    font_family: 'Inter, sans-serif',
    font_color: '#000000',
    logo_url: '',
    business_intro: '',
    currency: 'USD',
    currency_symbol: '$',
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const response = await axios.get(`${API}/vendor/theme`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.theme && Object.keys(response.data.theme).length > 0) {
        setTheme({
          background_color: response.data.theme.background_color || '#FFFFFF',
          background_image: response.data.theme.background_image || '',
          font_family: response.data.theme.font_family || 'Inter, sans-serif',
          font_color: response.data.theme.font_color || '#000000',
          logo_url: response.data.theme.logo_url || '',
          business_intro: response.data.theme.business_intro || '',
          currency: response.data.theme.currency || 'USD',
          currency_symbol: response.data.theme.currency_symbol || '₹',
        });
      }
    } catch (error) {
      console.error('Failed to fetch theme');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.put(`${API}/vendor/theme`, theme, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Theme updated successfully!');
    } catch (error) {
      toast.error('Failed to update theme');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (2MB)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Logo size must be less than 2MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setTheme(prev => ({ ...prev, logo_url: response.data.image_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="theme-customization-page">
      <div className="mb-8">
        <h2 className="text-3xl font-bold tracking-tight">Store Theme</h2>
        <p className="text-muted-foreground mt-2">Customize the look and feel of your storefront</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Settings */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Background Settings</CardTitle>
              <CardDescription>Choose your store's background appearance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Background Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={theme.background_color}
                    onChange={(e) => setTheme({ ...theme, background_color: e.target.value })}
                    className="w-20 h-10"
                    data-testid="bg-color-picker"
                  />
                  <Input
                    type="text"
                    value={theme.background_color}
                    onChange={(e) => setTheme({ ...theme, background_color: e.target.value })}
                    placeholder="#FFFFFF"
                    className="flex-1"
                  />
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color.value}
                      className="w-8 h-8 rounded-md border-2 border-gray-300 hover:border-primary transition-colors"
                      style={{ backgroundColor: color.value }}
                      onClick={() => setTheme({ ...theme, background_color: color.value })}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Background Image URL (Optional)</Label>
                <Input
                  type="text"
                  value={theme.background_image}
                  onChange={(e) => setTheme({ ...theme, background_image: e.target.value })}
                  placeholder="https://example.com/image.jpg or leave empty for default"
                  data-testid="bg-image-input"
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to use a beautiful default scenery background
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
              <CardDescription>Add your logo and business introduction</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Store Logo</Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <label htmlFor="logo-upload" className="flex-1">
                      <div className="flex items-center justify-center border-2 border-dashed rounded-lg p-4 hover:border-primary cursor-pointer transition-colors">
                        <Upload className="h-5 w-5 mr-2 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {uploadingLogo ? 'Uploading...' : 'Click to upload logo (Max 2 MB)'}
                        </span>
                      </div>
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                      />
                    </label>
                  </div>
                  {theme.logo_url && (
                    <div className="relative">
                      <img
                        src={theme.logo_url}
                        alt="Logo preview"
                        className="w-full h-32 object-contain bg-gray-50 rounded-lg p-2"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setTheme({ ...theme, logo_url: '' })}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                  <Input
                    type="text"
                    value={theme.logo_url}
                    onChange={(e) => setTheme({ ...theme, logo_url: e.target.value })}
                    placeholder="Or paste logo URL"
                    data-testid="logo-url-input"
                    className="text-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Upload your logo or paste a direct URL
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Business Introduction</Label>
                <Textarea
                  value={theme.business_intro}
                  onChange={(e) => setTheme({ ...theme, business_intro: e.target.value })}
                  placeholder="Tell your customers about your business..."
                  rows={4}
                  data-testid="business-intro-input"
                />
                <p className="text-xs text-muted-foreground">
                  A brief description that appears on your storefront
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Typography Settings</CardTitle>
              <CardDescription>Customize your store's text appearance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Font Family</Label>
                <Select
                  value={theme.font_family}
                  onValueChange={(value) => setTheme({ ...theme, font_family: value })}
                >
                  <SelectTrigger data-testid="font-family-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FONT_FAMILIES.map((font) => (
                      <SelectItem key={font.value} value={font.value}>
                        <span style={{ fontFamily: font.value }}>{font.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Text Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={theme.font_color}
                    onChange={(e) => setTheme({ ...theme, font_color: e.target.value })}
                    className="w-20 h-10"
                    data-testid="font-color-picker"
                  />
                  <Input
                    type="text"
                    value={theme.font_color}
                    onChange={(e) => setTheme({ ...theme, font_color: e.target.value })}
                    placeholder="#000000"
                    className="flex-1"
                  />
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {TEXT_COLORS.map((color) => (
                    <button
                      key={color.value}
                      className="w-8 h-8 rounded-md border-2 border-gray-300 hover:border-primary transition-colors"
                      style={{ backgroundColor: color.value }}
                      onClick={() => setTheme({ ...theme, font_color: color.value })}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Currency Settings</CardTitle>
              <CardDescription>Choose your store's currency</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select
                  value={theme.currency}
                  onValueChange={(value) => {
                    const currencies = {
                      'USD': '$',
                      'EUR': '€',
                      'GBP': '£',
                      'INR': '₹',
                      'JPY': '¥',
                      'AUD': 'A$',
                      'CAD': 'C$',
                      'CHF': 'CHF',
                      'CNY': '¥',
                      'AED': 'AED',
                      'SAR': 'SAR',
                      'ZAR': 'R',
                      'BRL': 'R$',
                      'MXN': '$',
                      'SGD': 'S$',
                    };
                    setTheme({ 
                      ...theme, 
                      currency: value,
                      currency_symbol: currencies[value] || value
                    });
                  }}
                >
                  <SelectTrigger data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD - US Dollar ($)</SelectItem>
                    <SelectItem value="EUR">EUR - Euro (€)</SelectItem>
                    <SelectItem value="GBP">GBP - British Pound (£)</SelectItem>
                    <SelectItem value="INR">INR - Indian Rupee (₹)</SelectItem>
                    <SelectItem value="JPY">JPY - Japanese Yen (¥)</SelectItem>
                    <SelectItem value="AUD">AUD - Australian Dollar (A$)</SelectItem>
                    <SelectItem value="CAD">CAD - Canadian Dollar (C$)</SelectItem>
                    <SelectItem value="CHF">CHF - Swiss Franc (CHF)</SelectItem>
                    <SelectItem value="CNY">CNY - Chinese Yuan (¥)</SelectItem>
                    <SelectItem value="AED">AED - UAE Dirham (AED)</SelectItem>
                    <SelectItem value="SAR">SAR - Saudi Riyal (SAR)</SelectItem>
                    <SelectItem value="ZAR">ZAR - South African Rand (R)</SelectItem>
                    <SelectItem value="BRL">BRL - Brazilian Real (R$)</SelectItem>
                    <SelectItem value="MXN">MXN - Mexican Peso ($)</SelectItem>
                    <SelectItem value="SGD">SGD - Singapore Dollar (S$)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Selected: {theme.currency} ({theme.currency_symbol})
                </p>
              </div>
            </CardContent>
          </Card>

          <Button onClick={handleSave} className="w-full" disabled={loading} data-testid="save-theme-btn">
            {loading ? 'Saving...' : 'Save Theme'}
          </Button>
        </div>

        {/* Sticky Preview */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <Card>
            <CardHeader>
              <CardTitle>Live Preview</CardTitle>
              <CardDescription>See how your store will look in real-time</CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="rounded-lg border overflow-hidden"
                style={{
                  minHeight: '500px',
                  backgroundColor: theme.background_color || '#FFFFFF',
                  backgroundImage: theme.background_image
                    ? `url(${theme.background_image})`
                    : (!theme.background_color || theme.background_color === '#FFFFFF' || theme.background_color.toLowerCase() === '#fff' || theme.background_color === 'rgb(255, 255, 255)')
                    ? `url("https://images.unsplash.com/photo-1557683316-973673baf926?w=1200&h=800&fit=crop&q=80")`
                    : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  fontFamily: theme.font_family || 'Inter, sans-serif',
                  color: theme.font_color || '#000000',
                }}
              >
                <div className="p-8">
                  <div style={{ 
                    backgroundColor: theme.background_color === '#FFFFFF' || theme.background_color.toLowerCase() === '#fff' 
                      ? 'rgba(255, 255, 255, 0.95)' 
                      : theme.background_color === '#000000' || theme.background_color.toLowerCase() === '#000'
                      ? 'rgba(0, 0, 0, 0.85)'
                      : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(12px)',
                    borderRadius: '0.5rem',
                    padding: '1.5rem',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                    maxWidth: '28rem',
                    border: `1px solid ${theme.font_color}20`
                  }}>
                    <h3 
                      className="text-2xl font-bold mb-2" 
                      style={{ 
                        color: theme.font_color,
                        fontFamily: theme.font_family 
                      }}
                    >
                      Your Store Name
                    </h3>
                    <p 
                      className="mb-4 text-sm" 
                      style={{ 
                        color: theme.font_color, 
                        opacity: 0.7,
                        fontFamily: theme.font_family 
                      }}
                    >
                      This is how your store will appear to customers
                    </p>
                    <div className="space-y-4">
                      <div 
                        className="rounded-lg p-4"
                        style={{
                          backgroundColor: theme.background_color === '#000000' || theme.background_color.toLowerCase() === '#000'
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.05)',
                          border: `1px solid ${theme.font_color}15`
                        }}
                      >
                        <div 
                          className="font-medium mb-1" 
                          style={{ 
                            color: theme.font_color,
                            fontFamily: theme.font_family
                          }}
                        >
                          Sample Product
                        </div>
                        <div 
                          style={{ 
                            color: theme.font_color, 
                            opacity: 0.6, 
                            fontSize: '0.875rem',
                            fontFamily: theme.font_family
                          }}
                        >
                          Product description goes here
                        </div>
                        <div 
                          className="mt-2 font-bold text-lg" 
                          style={{ 
                            color: theme.font_color,
                            fontFamily: theme.font_family
                          }}
                        >
                          {theme.currency_symbol}29.99
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ThemeCustomization;
